/**
 * Probe — the contracts between the Probe page, the workspace server and
 * OpenAI's GPT-Live.
 *
 * Two families live here, and they are kept apart on purpose:
 *
 * - **Probe's own routes** (`/api/amoreg/probe/*`): what the browser asks the
 *   workspace server for and what it gets back. These are ours, versioned
 *   with the app.
 * - **GPT-Live's data channel** (`Live*`): the subset of OpenAI's server
 *   events the page acts on, decoded at the edge so nothing past the decoder
 *   handles `unknown`. Events the page has no use for fail to decode and are
 *   dropped there — the vendor adds event types faster than we need them.
 *
 * Field names in the `Live*` schemas are OpenAI's (snake_case); everything
 * else is camelCase like the rest of the app.
 *
 * @module probe
 */
import * as Schema from "effect/Schema";

// ── Voices ─────────────────────────────────────────────────────────────

/**
 * The English GPT-Live voices, by API name. Portuguese-only voices (`bossa`,
 * `tempo`) are deliberately absent: Probe opens in English and switches
 * language only when the person does, so every voice it offers must be
 * native in English.
 */
export const PROBE_VOICES = [
  "vesper",
  "marin",
  "cedar",
  "willow",
  "stone",
  "quartz",
  "ripple",
  "gleam",
  "meridian",
  "delta",
  "cinder",
  "beacon",
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "sage",
  "shimmer",
  "verse",
] as const;
export const ProbeVoice = Schema.Literals(PROBE_VOICES);
export type ProbeVoice = typeof ProbeVoice.Type;

/**
 * How the examiner gives feedback: `guided` after every answer, `real` only
 * in a debrief once the person says they have finished.
 */
export const PROBE_MODES = ["guided", "real"] as const;
export const ProbeMode = Schema.Literals(PROBE_MODES);
export type ProbeMode = typeof ProbeMode.Type;

// ── Probe's routes ─────────────────────────────────────────────────────

export const ProbeJurisdiction = Schema.Struct({
  slug: Schema.String,
  name: Schema.String,
});
export type ProbeJurisdiction = typeof ProbeJurisdiction.Type;

/** The person the examiner will be speaking with, as the organization records them. */
export const ProbePerson = Schema.Struct({
  email: Schema.String,
  name: Schema.NullOr(Schema.String),
  jobTitle: Schema.NullOr(Schema.String),
});
export type ProbePerson = typeof ProbePerson.Type;

/**
 * An open finding the examiner has read. `id` is the finding's own (null only
 * when the row came without one), so the page can open it.
 */
export const ProbeBriefingFinding = Schema.Struct({
  id: Schema.NullOr(Schema.String),
  severity: Schema.NullOr(Schema.String),
  title: Schema.String,
  /** As much of the summary as the examiner reads. */
  summary: Schema.NullOr(Schema.String),
});
export type ProbeBriefingFinding = typeof ProbeBriefingFinding.Type;

/** A regulator publication the monitor recorded. */
export const ProbeBriefingUpdate = Schema.Struct({
  id: Schema.NullOr(Schema.String),
  /** `YYYY-MM-DD`, publication or else detection. */
  date: Schema.NullOr(Schema.String),
  jurisdiction: Schema.NullOr(Schema.String),
  title: Schema.String,
  url: Schema.NullOr(Schema.String),
});
export type ProbeBriefingUpdate = typeof ProbeBriefingUpdate.Type;

/** A document in the policy library; `id` is the policy's. */
export const ProbeBriefingLibraryEntry = Schema.Struct({
  id: Schema.NullOr(Schema.String),
  name: Schema.String,
  /** Kind, lifecycle, owner and review date, as the examiner reads them. */
  facts: Schema.String,
});
export type ProbeBriefingLibraryEntry = typeof ProbeBriefingLibraryEntry.Type;

/** The longest passage text the briefing keeps; a longer one is cut to end in "…". */
export const PROBE_BRIEFING_PASSAGE_MAX_CHARS = 360;

/** A knowledge-base passage on what one jurisdiction's authority expects. */
export const ProbeBriefingPassage = Schema.Struct({
  jurisdiction: Schema.String,
  source: Schema.NullOr(Schema.String),
  text: Schema.String,
});
export type ProbeBriefingPassage = typeof ProbeBriefingPassage.Type;

/** The file's records, one per line of `ProbeBriefingFile`'s lists and in the same order. */
export const ProbeBriefingItems = Schema.Struct({
  findings: Schema.Array(ProbeBriefingFinding),
  updates: Schema.Array(ProbeBriefingUpdate),
  authority: Schema.Array(ProbeBriefingPassage),
  library: Schema.Array(ProbeBriefingLibraryEntry),
});
export type ProbeBriefingItems = typeof ProbeBriefingItems.Type;

/**
 * `GET /api/amoreg/probe/briefing` — what the examiner has read before the
 * call. Asking for it also warms the server's copy, so pressing Start costs
 * one SDP exchange rather than a round of record reads.
 */
/**
 * What the examiner has on file, as the page shows it: the same lines the
 * examiner reads, one per fact. Each list may be empty when its read
 * failed or found nothing.
 */
export const ProbeBriefingFile = Schema.Struct({
  /** Findings still open, most severe first. */
  findings: Schema.Array(Schema.String),
  /** Regulator publications of the last few months. */
  updates: Schema.Array(Schema.String),
  /** What the knowledge base says each authority expects, by jurisdiction. */
  authority: Schema.Array(Schema.String),
  /** The policy library, one line per document. */
  library: Schema.Array(Schema.String),
  /** Documents in the library, including any beyond the lines above. */
  libraryTotal: Schema.Number,
  /** The same facts as records with their ids; absent from older servers, which send only the lines. */
  items: Schema.optionalKey(ProbeBriefingItems),
});
export type ProbeBriefingFile = typeof ProbeBriefingFile.Type;

/**
 * The code in a 403's body when the organization is limited to approved AI
 * providers and Probe's voice service is not one of them. Older servers sent
 * only the English message.
 */
export const PROBE_RESTRICTED_PROVIDERS = "probe-restricted-providers";
export const PROBE_RESTRICTED_PROVIDERS_MESSAGE =
  "Voice interviews are unavailable for this workspace's approved AI providers.";

/** A refusal Probe's routes word themselves, rather than passing the domain API's through. */
export const ProbeRefusal = Schema.Struct({
  code: Schema.Literal(PROBE_RESTRICTED_PROVIDERS),
  message: Schema.String,
});
export type ProbeRefusal = typeof ProbeRefusal.Type;

export const ProbeBriefing = Schema.Struct({
  organizationName: Schema.String,
  person: ProbePerson,
  jurisdictions: Schema.Array(ProbeJurisdiction),
  /** Past rehearsals by this person the examiner has summaries of. */
  rehearsals: Schema.Number,
  defaultVoice: ProbeVoice,
  /**
   * False when this server cannot start a call (no OpenAI key): the file is
   * still read and shown, but Start would fail. Absent from older servers,
   * which only answered when they could.
   */
  configured: Schema.optionalKey(Schema.Boolean),
  /** The examiner's file; absent from older servers. */
  file: Schema.optionalKey(ProbeBriefingFile),
});
export type ProbeBriefing = typeof ProbeBriefing.Type;

/** `POST /api/amoreg/probe/session` — the browser's WebRTC offer, chosen voice and mode. */
export const ProbeSessionRequest = Schema.Struct({
  sdp: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(65_536)),
  voice: ProbeVoice,
  /** Absent from older pages, which get a guided interview. */
  mode: Schema.optionalKey(ProbeMode),
});
export type ProbeSessionRequest = typeof ProbeSessionRequest.Type;

/**
 * The answer to an offer: OpenAI's SDP, and the session it started. The
 * browser never sees the OpenAI key or the session configuration — the
 * server created the session with both.
 */
export const ProbeSessionGrant = Schema.Struct({
  sessionId: Schema.String,
  sdp: Schema.String,
  model: Schema.String,
  voice: ProbeVoice,
  jurisdictions: Schema.Array(ProbeJurisdiction),
});
export type ProbeSessionGrant = typeof ProbeSessionGrant.Type;

/**
 * `POST /api/amoreg/probe/tool` — one function call the backend model asked
 * for. `arguments` is the model's raw JSON string, or an already-parsed
 * object; the server decodes it against the tool's own contract.
 */
export const ProbeToolRequest = Schema.Struct({
  name: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
  arguments: Schema.Unknown,
  /**
   * The GPT-Live session the call belongs to, so a per-interview limit can
   * count it. The page supplies it; the organization's daily limit is the
   * one the server holds without trusting the page.
   */
  sessionId: Schema.optional(Schema.String.check(Schema.isMaxLength(200))),
});
export type ProbeToolRequest = typeof ProbeToolRequest.Type;

/** A tool's JSON output, or a refusal written for the model to read. */
export const ProbeToolResult = Schema.Union([
  Schema.Struct({ output: Schema.Unknown }),
  Schema.Struct({ error: Schema.String }),
]);
export type ProbeToolResult = typeof ProbeToolResult.Type;

/** The one Probe tool that is not a governance read: a search of the open web. */
export const PROBE_WEB_SEARCH_TOOL = "probe_web_search";

/**
 * The governance tools Probe's backend may call. Reads only: a voice agent
 * that changes review cadences or decides findings because it misheard
 * "resolve" is worse than one that has to send you to the page. Widening
 * this list is a deliberate act, not a default.
 */
export const PROBE_GOVERNANCE_TOOL_NAMES = [
  "governance_get_context",
  "governance_list_policies",
  "governance_get_policy",
  "governance_get_policy_text",
  "governance_list_regulatory_updates",
  "governance_search_knowledge",
  "governance_list_findings",
  "governance_list_people",
  "governance_list_recommendations",
  "governance_list_probe_interviews",
  "governance_get_probe_interview",
] as const;
export type ProbeGovernanceToolName = (typeof PROBE_GOVERNANCE_TOOL_NAMES)[number];

/**
 * Everything Probe's backend may call: the governance reads, and a search
 * of the open web, which reads nothing of the organization's and changes
 * nothing anywhere.
 */
export const PROBE_TOOL_NAMES = [...PROBE_GOVERNANCE_TOOL_NAMES, PROBE_WEB_SEARCH_TOOL] as const;
export type ProbeToolName = (typeof PROBE_TOOL_NAMES)[number];

/** One page a Probe web search found: what the backend reads, and what the page links. */
export const ProbeWebSource = Schema.Struct({
  title: Schema.String,
  url: Schema.String,
  /** `YYYY-MM-DD`, when the search engine could tell. */
  publishedDate: Schema.NullOr(Schema.String),
  snippet: Schema.String,
});
export type ProbeWebSource = typeof ProbeWebSource.Type;

/** `probe_web_search`'s output. */
export const ProbeWebSearchOutput = Schema.Struct({ results: Schema.Array(ProbeWebSource) });
export type ProbeWebSearchOutput = typeof ProbeWebSearchOutput.Type;

/** The longest turn a saved interview keeps. */
export const PROBE_TURN_MAX_CHARS = 20_000;
/** The most turns a saved interview keeps. */
export const PROBE_INTERVIEW_MAX_TURNS = 600;
/** The most web pages a saved turn links. */
export const PROBE_TURN_MAX_SOURCES = 10;
/** The longest page title a saved turn keeps. */
export const PROBE_SOURCE_TITLE_MAX_CHARS = 300;
/** The longest page address a saved turn keeps; a longer one is dropped, never cut. */
export const PROBE_SOURCE_URL_MAX_CHARS = 2_000;

/**
 * A page the examiner checked on the web, kept with the turn it was linked
 * under. Only http(s) addresses: the history draws them as links.
 */
export const ProbeTurnSource = Schema.Struct({
  title: Schema.String.check(Schema.isMaxLength(PROBE_SOURCE_TITLE_MAX_CHARS)),
  url: Schema.String.check(
    Schema.isMaxLength(PROBE_SOURCE_URL_MAX_CHARS),
    Schema.isPattern(/^https?:\/\/\S+$/i),
  ),
});
export type ProbeTurnSource = typeof ProbeTurnSource.Type;

/** The most lookups a saved turn keeps. */
export const PROBE_TURN_MAX_LOOKUPS = 20;
/** The most records a saved lookup keeps; a longer list keeps its first rows. */
export const PROBE_LOOKUP_MAX_RECORDS = 60;
/** The most lookups a saved interview keeps across all its turns. */
export const PROBE_INTERVIEW_MAX_LOOKUPS = 200;
/** The most records a saved interview keeps across all its turns. */
export const PROBE_INTERVIEW_MAX_RECORDS = 300;
/** The longest record id, title, source or argument a saved lookup keeps. */
export const PROBE_RECORD_FIELD_MAX_CHARS = 300;
/** The longest reason a saved failed lookup keeps. */
export const PROBE_LOOKUP_REASON_MAX_CHARS = 300;

/** The longest interview a save records the length of: a working day. */
export const PROBE_INTERVIEW_MAX_DURATION_MS = 12 * 60 * 60 * 1000;

const RecordField = Schema.String.check(Schema.isMaxLength(PROBE_RECORD_FIELD_MAX_CHARS));
const HttpAddress = ProbeTurnSource.fields.url;

/** What the library holds about a policy, as the desk reads it out. */
export const ProbePolicyFacts = Schema.Struct({
  lifecycle: Schema.NullOr(RecordField),
  owner: Schema.NullOr(RecordField),
  reviewDue: Schema.NullOr(RecordField),
});
export type ProbePolicyFacts = typeof ProbePolicyFacts.Type;

/**
 * A record one of the examiner's lookups returned, by the id the server gave
 * it. A web page's id is its address; a knowledge passage's is a hash of its
 * source and text, since passages have none.
 */
export const ProbeRecord = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("finding"),
    id: RecordField,
    title: RecordField,
    severity: Schema.NullOr(RecordField),
    status: RecordField,
  }),
  Schema.Struct({
    kind: Schema.Literal("policy"),
    id: RecordField,
    /** Null when the lookup returned only the document's text, which does not name it. */
    title: Schema.NullOr(RecordField),
    facts: Schema.NullOr(ProbePolicyFacts),
  }),
  Schema.Struct({
    kind: Schema.Literal("update"),
    id: RecordField,
    title: RecordField,
    date: Schema.NullOr(RecordField),
    jurisdiction: RecordField,
    url: Schema.NullOr(HttpAddress),
  }),
  Schema.Struct({
    kind: Schema.Literal("passage"),
    id: RecordField,
    title: RecordField,
    source: Schema.NullOr(RecordField),
    text: Schema.String.check(Schema.isMaxLength(PROBE_BRIEFING_PASSAGE_MAX_CHARS)),
  }),
  Schema.Struct({
    kind: Schema.Literal("rehearsal"),
    id: RecordField,
    title: RecordField,
    date: RecordField,
  }),
  Schema.Struct({
    kind: Schema.Literal("web"),
    id: HttpAddress,
    title: RecordField,
    url: HttpAddress,
  }),
]);
export type ProbeRecord = typeof ProbeRecord.Type;

/** The arguments the page names a lookup by; any other argument is not kept. */
export const ProbeLookupArguments = Schema.Struct({
  policyId: Schema.optionalKey(RecordField),
  interviewId: Schema.optionalKey(RecordField),
  question: Schema.optionalKey(RecordField),
  query: Schema.optionalKey(RecordField),
  jurisdiction: Schema.optionalKey(RecordField),
  status: Schema.optionalKey(RecordField),
  author: Schema.optionalKey(RecordField),
  offset: Schema.optionalKey(Schema.Finite),
  withinDays: Schema.optionalKey(Schema.Finite),
});
export type ProbeLookupArguments = typeof ProbeLookupArguments.Type;

/**
 * A lookup that finished before its turn was spoken, with the records it
 * returned: what the page needs to show what the examiner had read by then.
 */
export const ProbeTurnLookup = Schema.Struct({
  name: Schema.String.check(Schema.isMaxLength(120)),
  arguments: ProbeLookupArguments,
  status: Schema.Literals(["done", "failed"]),
  /** Why a failed lookup failed, as the server worded it for the examiner. */
  reason: Schema.NullOr(Schema.String.check(Schema.isMaxLength(PROBE_LOOKUP_REASON_MAX_CHARS))),
  /** Where the session timeline was when it finished. */
  atMs: Schema.Number.check(
    Schema.isInt(),
    Schema.isBetween({ minimum: 0, maximum: PROBE_INTERVIEW_MAX_DURATION_MS }),
  ),
  records: Schema.Array(ProbeRecord).check(Schema.isMaxLength(PROBE_LOOKUP_MAX_RECORDS)),
});
export type ProbeTurnLookup = typeof ProbeTurnLookup.Type;

export const ProbeTurn = Schema.Struct({
  role: Schema.Literals(["user", "assistant"]),
  text: Schema.String.check(Schema.isMaxLength(PROBE_TURN_MAX_CHARS)),
  /** The web pages linked under this turn; absent when there were none (and on older saves). */
  sources: Schema.optionalKey(
    Schema.Array(ProbeTurnSource).check(Schema.isMaxLength(PROBE_TURN_MAX_SOURCES)),
  ),
  /** The lookups that finished before this turn and after the one before; absent when none did (and on older saves). */
  lookups: Schema.optionalKey(
    Schema.Array(ProbeTurnLookup).check(Schema.isMaxLength(PROBE_TURN_MAX_LOOKUPS)),
  ),
});
export type ProbeTurn = typeof ProbeTurn.Type;

/**
 * `POST /api/amoreg/probe/history` — a finished interview. The server
 * writes the summary and the debrief; the browser only reports what was
 * said, and how long the conversation ran.
 */
export const ProbeInterviewSave = Schema.Struct({
  title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
  turns: Schema.Array(ProbeTurn).check(
    Schema.isMinLength(2),
    Schema.isMaxLength(PROBE_INTERVIEW_MAX_TURNS),
    Schema.makeFilter(
      (turns: ReadonlyArray<ProbeTurn>) =>
        turns.flatMap((turn) => turn.lookups ?? []).length <= PROBE_INTERVIEW_MAX_LOOKUPS ||
        `at most ${PROBE_INTERVIEW_MAX_LOOKUPS} lookups across the turns`,
    ),
    Schema.makeFilter(
      (turns: ReadonlyArray<ProbeTurn>) =>
        turns.flatMap((turn) => turn.lookups ?? []).flatMap((lookup) => lookup.records).length <=
          PROBE_INTERVIEW_MAX_RECORDS ||
        `at most ${PROBE_INTERVIEW_MAX_RECORDS} records across the turns`,
    ),
  ),
  /**
   * From the call's first word to its last, on the session's own clock.
   * Absent from older pages.
   */
  durationMs: Schema.optionalKey(
    Schema.Number.check(
      Schema.isInt(),
      Schema.isBetween({ minimum: 0, maximum: PROBE_INTERVIEW_MAX_DURATION_MS }),
    ),
  ),
});
export type ProbeInterviewSave = typeof ProbeInterviewSave.Type;

// ── The debrief ────────────────────────────────────────────────────────

/**
 * The areas a real examination covers — the examiner's topic index, and
 * the fixed vocabulary its debrief rates answers against, so weak spots can
 * be counted across interviews.
 */
export const PROBE_EXAMINATION_AREAS = [
  "governance and board oversight",
  "capital adequacy and liquidity",
  "financial reporting and client money",
  "AML/CFT and sanctions",
  "risk management and the risk appetite",
  "conduct, consumer outcomes and complaints",
  "outsourcing and third parties",
  "operational resilience and business continuity",
  "IT and cyber security",
  "data protection",
  "fitness and propriety, competence and training",
  "breach reporting and regulatory returns",
] as const;
export const ProbeExaminationArea = Schema.Literals(PROBE_EXAMINATION_AREAS);
export type ProbeExaminationArea = typeof ProbeExaminationArea.Type;

/** How the answers in one area held up. */
export const PROBE_AREA_ASSESSMENTS = ["strong", "adequate", "weak"] as const;
export const ProbeAreaAssessment = Schema.Literals(PROBE_AREA_ASSESSMENTS);
export type ProbeAreaAssessment = typeof ProbeAreaAssessment.Type;

/** The interview as a whole, as the examiner would leave it. */
export const PROBE_OUTCOMES = ["strong", "mixed", "weak"] as const;
export const ProbeOutcome = Schema.Literals(PROBE_OUTCOMES);
export type ProbeOutcome = typeof ProbeOutcome.Type;

export const PROBE_DEBRIEF_NOTE_MAX_CHARS = 300;
export const PROBE_DEBRIEF_MAX_FOLLOW_UPS = 8;

export const ProbeAreaDebrief = Schema.Struct({
  area: ProbeExaminationArea,
  assessment: ProbeAreaAssessment,
  /** Why, in a sentence: what was said or missing. */
  note: Schema.String.check(Schema.isMaxLength(PROBE_DEBRIEF_NOTE_MAX_CHARS)),
});
export type ProbeAreaDebrief = typeof ProbeAreaDebrief.Type;

/**
 * The structured half of a saved interview's summary, written by the same
 * model call: the outcome, each area the interview covered with how it
 * went, and the evidence or follow-ups the person promised or still owes.
 * Interviews saved before debriefs existed — or whose summariser failed —
 * have none.
 */
export const ProbeDebrief = Schema.Struct({
  outcome: ProbeOutcome,
  areas: Schema.Array(ProbeAreaDebrief).check(Schema.isMaxLength(PROBE_EXAMINATION_AREAS.length)),
  followUps: Schema.Array(
    Schema.String.check(Schema.isMaxLength(PROBE_DEBRIEF_NOTE_MAX_CHARS)),
  ).check(Schema.isMaxLength(PROBE_DEBRIEF_MAX_FOLLOW_UPS)),
});
export type ProbeDebrief = typeof ProbeDebrief.Type;

// ── The review ─────────────────────────────────────────────────────────

/** The most weak moments a review points at. */
export const PROBE_REVIEW_MAX_MOMENTS = 3;
/** The longest reason a moment fell short. */
export const PROBE_REVIEW_REASON_MAX_CHARS = 240;
/** The longest account of what a strong answer covers. */
export const PROBE_REVIEW_STRONGER_MAX_CHARS = 400;
/** The longest next step. */
export const PROBE_REVIEW_NEXT_STEP_MAX_CHARS = 240;
/** The longest title a review gives its interview; a longer one is dropped, never cut. */
export const PROBE_REVIEW_TITLE_MAX_CHARS = 40;
/**
 * How long a review may stay `pending` before it reads as `failed`. Writing
 * one takes well under this; a review still pending after it was lost (a
 * server restart mid-run) and the page offers a retry.
 */
export const PROBE_REVIEW_STALE_MS = 5 * 60 * 1000;

/**
 * One answer that fell short. `turn` is the interviewee's turn by its
 * position in the saved turns, never a quote: the page shows the words that
 * were saved, so the review cannot put words in anyone's mouth.
 */
export const ProbeReviewMoment = Schema.Struct({
  area: ProbeExaminationArea,
  turn: Schema.Number.check(
    Schema.isInt(),
    Schema.isBetween({ minimum: 0, maximum: PROBE_INTERVIEW_MAX_TURNS - 1 }),
  ),
  /** Why the answer fell short, in a sentence. */
  reason: Schema.String.check(
    Schema.isMinLength(1),
    Schema.isMaxLength(PROBE_REVIEW_REASON_MAX_CHARS),
  ),
  /**
   * What a strong answer would cover: illustrative, drawn only from what the
   * person said and the records read during the call.
   */
  strongerAnswer: Schema.String.check(
    Schema.isMinLength(1),
    Schema.isMaxLength(PROBE_REVIEW_STRONGER_MAX_CHARS),
  ),
});
export type ProbeReviewMoment = typeof ProbeReviewMoment.Type;

/** When the review was asked for (ISO 8601, UTC): which run a written review belongs to. */
const ReviewRequestedAt = Schema.String.check(
  Schema.isPattern(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/),
);

/**
 * The review written after an interview is saved, in the background: the
 * moments the person slipped and one next step. `pending` until it lands,
 * `failed` when the model was late or wrote nothing usable (or the run was
 * lost), `ready` with the review. The domain API's interview read carries
 * it as `review`; interviews saved before reviews existed have none.
 */
export const ProbeReview = Schema.Union([
  Schema.Struct({ status: Schema.Literal("pending"), requestedAt: ReviewRequestedAt }),
  Schema.Struct({ status: Schema.Literal("failed"), requestedAt: ReviewRequestedAt }),
  Schema.Struct({
    status: Schema.Literal("ready"),
    requestedAt: ReviewRequestedAt,
    /** Most important first; empty when every answer held up. */
    moments: Schema.Array(ProbeReviewMoment).check(Schema.isMaxLength(PROBE_REVIEW_MAX_MOMENTS)),
    /** One imperative sentence. */
    nextStep: Schema.String.check(
      Schema.isMinLength(1),
      Schema.isMaxLength(PROBE_REVIEW_NEXT_STEP_MAX_CHARS),
    ),
    /**
     * What was rehearsed in a few words, sentence case ("PEP screening of
     * nominees"), for lists that would otherwise show the opening question.
     * Absent when the reviewer gave none that fits.
     */
    title: Schema.optionalKey(
      Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(PROBE_REVIEW_TITLE_MAX_CHARS)),
    ),
  }),
]);
export type ProbeReview = typeof ProbeReview.Type;

// ── GPT-Live: session creation (server ↔ OpenAI) ───────────────────────

/** `POST /v1/live/sessions` with a WebRTC offer answers with this. */
export const LiveSessionCreated = Schema.Struct({
  session: Schema.Struct({ id: Schema.String }),
  transport: Schema.Struct({ type: Schema.Literal("webrtc"), sdp: Schema.String }),
});
export type LiveSessionCreated = typeof LiveSessionCreated.Type;

// ── GPT-Live: data-channel server events (OpenAI → browser) ────────────

export const LiveSessionStarted = Schema.Struct({
  type: Schema.Literal("session.started"),
  session: Schema.Struct({ id: Schema.String }),
});

/**
 * A fragment of what someone said, placed on the session timeline. GPT-Live
 * is full duplex, so both speakers' fragments can arrive interleaved; there
 * is no "turn done" event — grouping is the reader's job.
 */
export const LiveTranscriptDelta = Schema.Struct({
  type: Schema.Literals(["session.input_transcript.delta", "session.output_transcript.delta"]),
  delta: Schema.String,
  start_ms: Schema.Number,
  end_ms: Schema.Number,
});
export type LiveTranscriptDelta = typeof LiveTranscriptDelta.Type;

/** The voice model handed work to the backend. Metadata only — no task text. */
export const LiveDelegationCreated = Schema.Struct({
  type: Schema.Literal("session.delegation.created"),
  offset_ms: Schema.Number,
  delegation: Schema.Struct({
    id: Schema.String,
    target: Schema.Literals(["client", "responses"]),
    response_id: Schema.optionalKey(Schema.String),
  }),
});

/**
 * A Responses stream event from the delegated backend, wrapped. The inner
 * event is decoded separately (`ResponsesBackendEvent`) — most of them are
 * deltas the page does not need.
 */
export const LiveResponseEvent = Schema.Struct({
  type: Schema.Literal("response.event"),
  delegation_id: Schema.optionalKey(Schema.NullOr(Schema.String)),
  event: Schema.Record(Schema.String, Schema.Unknown),
});

export const LiveSessionClosed = Schema.Struct({
  type: Schema.Literal("session.closed"),
  reason: Schema.optionalKey(Schema.String),
});

export const LiveError = Schema.Struct({
  type: Schema.Literal("error"),
  error: Schema.Struct({
    code: Schema.optionalKey(Schema.NullOr(Schema.String)),
    message: Schema.String,
    client_event_id: Schema.optionalKey(Schema.String),
  }),
});

/** Every data-channel event the Probe page acts on. */
export const LiveServerEvent = Schema.Union([
  LiveSessionStarted,
  LiveTranscriptDelta,
  LiveDelegationCreated,
  LiveResponseEvent,
  LiveSessionClosed,
  LiveError,
]);
export type LiveServerEvent = typeof LiveServerEvent.Type;

// ── GPT-Live: the delegated Responses backend's stream ─────────────────

/** A function call the backend model finished writing; it waits for our output. */
export const ResponsesFunctionCallDone = Schema.Struct({
  type: Schema.Literal("response.output_item.done"),
  item: Schema.Struct({
    type: Schema.Literal("function_call"),
    call_id: Schema.String,
    name: Schema.String,
    arguments: Schema.String,
  }),
});

/** A backend response reached a resting state; its calls can now be answered. */
export const ResponsesSettled = Schema.Struct({
  type: Schema.Literals([
    "response.completed",
    "response.failed",
    "response.incomplete",
    "response.cancelled",
  ]),
});

export const ResponsesBackendEvent = Schema.Union([ResponsesFunctionCallDone, ResponsesSettled]);
export type ResponsesBackendEvent = typeof ResponsesBackendEvent.Type;
